import type {
  Board,
  Enterprise,
  Member,
  Organization,
} from '@trello/model-types';

import { isAdminOfOrganization } from '../organization/isAdminOfOrganization';
import { isMemberOfOrganization } from '../organization/isMemberOfOrganization';
import { allowsSelfJoin } from './allowsSelfJoin';
import { getMembershipForBoard } from './getMembershipForBoard';

/**
 * This ports the `canJoin` method from `app/scripts/models/board.js`, which
 * relied on model cache data.
 */
export const canJoin = (
  workspace: Pick<Organization, 'id'> & {
    idEnterprise?: string | null;
    enterprise?: Pick<Organization['enterprise'], 'id'> | null;
    memberships: Pick<
      Organization['memberships'][number],
      'id' | 'idMember' | 'memberType' | 'deactivated' | 'unconfirmed'
    >[];
  },
  member: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  board: {
    idEnterprise?: string | null;
    memberships: Pick<
      Board['memberships'][number],
      'id' | 'idMember' | 'memberType'
    >[];
    organization?: Pick<Board['organization'], 'id'> | null;
    prefs?: Pick<Board['prefs'], 'selfJoin' | 'isTemplate'> | null;
  },
  enterprise?: Pick<Enterprise, 'idAdmins'> & { id: string },
) => {
  if (!workspace && !board.idEnterprise) {
    return false;
  }

  // Any board member type (admin, normal, or observer) can't join a board
  const memberType = getMembershipForBoard(board, member.id);
  if (memberType) {
    return false;
  }

  const isPremOrgAdmin = isAdminOfOrganization(member, workspace);
  const selfJoin = allowsSelfJoin(board);

  const isEnterpriseBoard = !!board.idEnterprise || !!workspace.enterprise;

  const isOrgMember = isMemberOfOrganization(member, workspace, enterprise);

  return (
    isPremOrgAdmin ||
    (selfJoin && isOrgMember) ||
    (isEnterpriseBoard &&
      enterprise &&
      enterprise.idAdmins?.includes(member.id))
  );
};
